import React from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import * as XLSX from 'xlsx';
import { RiFileExcel2Fill } from "react-icons/ri";
import { format } from 'date-fns';
import { obtenerTodosLosLibros } from '../../../services/LibrosService'; // Ajusta la ruta según tu estructura de carpetas
import './index.css'; // Asegúrate de crear este archivo para estilos personalizados

const DownloadInventarioExcel = () => {
    const MySwal = withReactContent(Swal);

    const handleDownloadExcel = async () => {
        const { isConfirmed, value: librosData } = await MySwal.fire({
            title: '¿Deseas descargar el inventario completo en Excel?',
            text: 'Esta operación puede tardar unos momentos.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, descargar',
            confirmButtonColor: '#21807C',
            cancelButtonText: 'Cancelar',
            showLoaderOnConfirm: true,
            preConfirm: async () => {
                try {
                    const data = await obtenerTodosLosLibros();
                    return data;
                } catch (error) {
                    Swal.showValidationMessage(
                        `Error al obtener los libros: ${error.response?.data?.mensaje || error.message || error}`
                    );
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });

        if (isConfirmed && librosData) {
            try {
                // Llamar a la función para generar el Excel con los datos obtenidos
                await generarExcel(librosData);
                Swal.fire({
                    title: '¡Excel generado!',
                    text: 'El archivo se ha descargado correctamente.',
                    icon: 'success',
                    confirmButtonColor: '#21807C',
                });
            } catch (error) {
                console.log('Error al generar el Excel:', error);
                Swal.fire({
                    title: 'Error',
                    text: 'No se pudo generar el Excel.',
                    icon: 'error',
                    confirmButtonColor: '#21807C',
                });
            }
        }
    };

    const generarExcel = async (librosData) => {
        try {
            // Mapear los datos según las columnas requeridas
            const libros = librosData.map(item => {
                const libro = item;

                // Formatear Melvil Dewey
                let melvilDewey = libro.categoriaDewey.codigoCategoria || 'N/A';
                if (libro.subcategoriaDewey && libro.subcategoriaDewey.codigoSubcategoria) {
                    melvilDewey += `.${libro.subcategoriaDewey.codigoSubcategoria}`;
                }


                // Concatenar Autor con código Cutter
                const autor = libro.autor.nombreAutor 
                    ? `${libro.autor.nombreAutor} - ${libro.autor.codigoCutter}` 
                    : 'N/A';

                // Concatenar Ubicación
                const ubicacion = libro.ubicacion 
                    ? `Area: ${libro.ubicacion.area}, Estante: ${libro.ubicacion.numEstante}, Repisa: ${libro.ubicacion.numRepisa}` 
                    : 'N/A';

                // Listar Palabras Clave
                const palabrasClave = libro.palabrasClave && libro.palabrasClave.length > 0 
                    ? libro.palabrasClave.join(', ') 
                    : 'N/A';

                // Listar Copias
                const copias = libro.copias && libro.copias.length > 0 
                    ? libro.copias.map(copia => copia.codigoCopia).join(', ') 
                    : 'N/A';

                // Concatenar Número de Llamada Local: Melvil Dewey + codigoCutter + nombreFondo
                const melvilDeweyCodigoCutter = (melvilDewey !== 'N/A' && libro.autor?.codigoCutter) 
                    ? `${melvilDewey} ${libro.autor.codigoCutter}` 
                    : (melvilDewey !== 'N/A' 
                        ? melvilDewey 
                        : (libro.autor?.codigoCutter || 'N/A'));

                const numeroLlamadaLocal = `${melvilDeweyCodigoCutter} ${libro.fondo?.nombreFondo || 'N/A'}`;

                return {
                    'Id del Libro': libro.idLibro || 'N/A',
                    'Titulo': libro.titulo || 'N/A',
                    'Autor': autor,
                    'Sinopsis': libro.sinopsis || 'N/A',
                    'Isbn': libro.isbn || 'N/A',
                    'Año de publicación': libro.añoPublicacion || 'N/A',
                    'Publicación y Distribución': libro.publicacionDistribucion || 'N/A',
                    'Melvil Dewey': melvilDewey || 'N/A',
                    'Número de Llamada Local': numeroLlamadaLocal,
                    'Fondo': libro.fondo.nombreFondo || 'N/A',
                    'Categoria': libro.categoria.nombreCategoria || 'N/A',
                    'Descripción Física': libro.descripcionFisica || 'N/A',
                    'Precio': libro.precio !== null && libro.precio !== undefined ? libro.precio : 'N/A',
                    'Calificación Promedio': libro.calificacionPromedio !== null && libro.calificacionPromedio !== undefined ? libro.calificacionPromedio : 'N/A',
                    'Prestable': libro.prestable ? 'Sí' : 'No',
                    'Ubicación': ubicacion,
                    'Palabras Clave': palabrasClave,
                    'Número de copias totales': libro.numCopiasTotales !== null && libro.numCopiasTotales !== undefined ? libro.numCopiasTotales : 'N/A',
                    'Número de copias disponibles': libro.numCopiasDisponibles !== null && libro.numCopiasDisponibles !== undefined ? libro.numCopiasDisponibles : 'N/A',
                    'Copias': copias,
                };
            });

            // Crear una hoja de Excel con los datos mapeados
            const worksheet = XLSX.utils.json_to_sheet(libros);

            // Crear un libro de Excel y agregar la hoja
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Inventario');

            // Generar el archivo Excel
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

            // Crear un Blob y descargar el archivo
            const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Inventario_${format(new Date(), 'dd-MM-yyyy')}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            throw error;
        }
    };

    return (
        <button onClick={handleDownloadExcel} className="download-excel-button">
            Inventario en Excel
            <RiFileExcel2Fill size={20} />
        </button>
    );
};

export default DownloadInventarioExcel;
